import SupplierPriceMatrixPreviewMutation from '../../graphql/mutations/supplier/SupplierPriceMatrixPreviewMutation.graphql';
import SupplierPriceMatrixActivateMutation from '../../graphql/mutations/supplier/SupplierPriceMatrixActivateMutation.graphql';
import SupplierPriceMatrixDeactivateMutation from '../../graphql/mutations/supplier/SupplierPriceMatrixDeactivateMutation.graphql';
import SupplierPriceMatricesActivateMutation from '../../graphql/mutations/supplier/SupplierPriceMatricesActivateMutation.graphql';
import SupplierPriceMatricesDeactivateMutation from '../../graphql/mutations/supplier/SupplierPriceMatricesDeactivateMutation.graphql';
import { InfoButton, SuccessButton } from '../../components/connectedForm/Button';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import getIn from '../../components/connectedForm/utils/getIn';
import getClientMutationId from '../../utils/getClientMutationId';
import { IWorkspaceAction } from '../../@types/workspaceTypes';
import { useP4ToastController } from '../../views/system/NotificationsArea';
import { useActionCriteria } from '../../utils/useActionProps';
import { useBackgroundMutation } from '../../utils/BackgroundJobs';
import { useCallback, useContext } from 'react';
import {
  AlertType,
  ISupplierPriceMatricesActivateMutationResult,
  ISupplierPriceMatricesActivateMutationVariables,
  ISupplierPriceMatricesDeactivateMutationResult,
  ISupplierPriceMatricesDeactivateMutationVariables,
} from '../../graphql/schema/p4types';
import { IDeprecatedFormAction } from '../../@types/entityTypes';
import WorkspaceContext from '../../components/Context/WorkspaceContext';

function queryVariablesFromProps(props) {
  return {
    variables: {
      input: {
        clientMutationId: getClientMutationId(),
        supplierPriceMatrix: normalizeVariables(props.values || props.formData.data),
      },
    },
  };
}

export const formActions: Array<IDeprecatedFormAction<any>> = [
  {
    description: 'Preview',
    component: InfoButton,
    fn: queryVariablesFromProps,
    individualButton: true,
    performTemporarySave: true,
    mutation: SupplierPriceMatrixPreviewMutation,
    mutationWrapper: {
      onCompleteMutation: (data) => {
        let job = getIn(data, 'generateSupplierPriceMatrixPreview.result');
        const path = `/Purchasing/SupplierPricing/SupplierPriceMatrix/Preview/Workspace/${job.jobId}/${job.properties.uuid}`;
        window.open(window.location.origin + path, '_blank');
      },
    },
  },
  {
    description: 'Activate',
    component: SuccessButton,
    fn: queryVariablesFromProps,
    individualButton: false,
    performSave: true,
    mutation: SupplierPriceMatrixActivateMutation,
    mutationWrapper: {
      subjectSuccess: 'Price matrix activated',
      messageSuccess: 'Price matrix has been activated.',
    },
  },
  {
    description: 'Deactivate',
    component: SuccessButton,
    fn: queryVariablesFromProps,
    individualButton: false,
    performSave: true,
    mutation: SupplierPriceMatrixDeactivateMutation,
    mutationWrapper: {
      subjectSuccess: 'Price matrix deactivated',
      messageSuccess: 'Price matrix has been deactivated.',
    },
  },
];

export const preview = [
  {
    description: 'Activate',
    call: queryVariablesFromProps,
    mutation: SupplierPriceMatrixActivateMutation,
    mutationWrapper: {
      subjectSuccess: 'Price matrix activated',
      messageSuccess: 'Price matrix has been activated.',
    },
  },
  {
    description: 'Deactivate',
    call: queryVariablesFromProps,
    mutation: SupplierPriceMatrixDeactivateMutation,
    mutationWrapper: {
      subjectSuccess: 'Price matrix deactivated',
      messageSuccess: 'Price matrix has been deactivated.',
    },
  },
];

enum ISupplierPriceMatrixWorkspaceAction {
  Activate = 'SUPPLIER_PRICE_MATRICES_ACTIVATE',
  Deactivate = 'SUPPLIER_PRICE_MATRICES_DEACTIVATE',
}

export const workspaceActionsForPermissions: Array<IWorkspaceAction<ISupplierPriceMatrixWorkspaceAction>> = [
  { actionReference: ISupplierPriceMatrixWorkspaceAction.Activate },
  { actionReference: ISupplierPriceMatrixWorkspaceAction.Deactivate },
];

export const useSupplierPriceMatrixWorkspaceActions = (): [
  actions: Array<IWorkspaceAction<ISupplierPriceMatrixWorkspaceAction>>
] => {
  const { dispatchSimpleToast } = useP4ToastController();
  const actionCriteria = useActionCriteria();
  const { reloadData } = useContext(WorkspaceContext);

  const [activatePriceMatrices] = useBackgroundMutation<
    ISupplierPriceMatricesActivateMutationResult,
    ISupplierPriceMatricesActivateMutationVariables
  >(SupplierPriceMatricesActivateMutation, {
    onCompleteJob: () => {
      dispatchSimpleToast('Supplier Price Matrix Activation', `Price matrices activated.`, AlertType.Success);
      reloadData?.();
    },
  });

  const onClickActivate = useCallback(() => {
    activatePriceMatrices({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          filters: actionCriteria.filters,
          supplierPriceMatrices: actionCriteria.actionEntities,
          include: actionCriteria.include,
        },
      },
    });
  }, [actionCriteria]);

  const [deactivatePriceMatrices] = useBackgroundMutation<
    ISupplierPriceMatricesDeactivateMutationResult,
    ISupplierPriceMatricesDeactivateMutationVariables
  >(SupplierPriceMatricesDeactivateMutation, {
    onCompleteJob: () => {
      dispatchSimpleToast('Supplier Price Matrix Deactivation', `Price matrices deactivated.`, AlertType.Success);
      reloadData?.();
    },
  });

  const onClickDeactivate = useCallback(() => {
    deactivatePriceMatrices({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          filters: actionCriteria.filters,
          supplierPriceMatrices: actionCriteria.actionEntities,
          include: actionCriteria.include,
        },
      },
    });
  }, [actionCriteria]);

  return [
    [
      {
        key: 'activate',
        text: 'Activate',
        onClick: onClickActivate,
        actionReference: ISupplierPriceMatrixWorkspaceAction.Activate,
      },
      {
        key: 'deactivate',
        text: 'Deactivate',
        onClick: onClickDeactivate,
        actionReference: ISupplierPriceMatrixWorkspaceAction.Deactivate,
      },
    ],
  ];
};
